<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            :columnDefs="columnDefs"
            :isAutoWidth="true"
            ref="grid"
            :rowData="rowData"
            :defaultColDef="defaultColDef"
            :autoGroupColumnDef="autoGroupColumnDef"
            :treeData="true"
            :getDataPath="getDataPath"
            :isGroupOpenByDefault="() => expanded"
        >
            <responsive-button :icon="['fas', 'plus']" variant="primary" @click="createResponse"
                >Add Response</responsive-button
            >
            <responsive-button
                :icon="['fas', 'up-right-and-down-left-from-center']"
                variant="primary"
                @click="onButtonExpand"
            >
                <span v-if="expanded">Collapse</span>
                <span v-else>Expand</span>
            </responsive-button>
        </grid>
    </page>
</template>

<script>
    import Page from '../generic/page/page.vue';
    import Grid from '../generic/grid/grid.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import { formatResponseError } from '../../utils/api';
    import api from '../../api';
    import { RESPONSES_DATA_ENDPOINT } from '../../endpoints';
    import { escapeHTML } from '../../utils/strings';

    export default {
        components: {
            Page,
            Grid,
            ResponsiveButton,
            actionsCellRenderer: ActionsCellRenderer,
        },
        data() {
            return {
                busy: false,
                rowData: [],
                defaultColDef: {
                    minWidth: 80,
                    sortable: false,
                },
                autoGroupColumnDef: {
                    flex: 2,
                    minWidth: 300,
                    resizable: true,
                    sortable: true,
                    comparator: (valueA, valueB, nodeA, nodeB) => {
                        const nameA = nodeA.data.title || nodeA.data.area_name;
                        const nameB = nodeB.data.title || nodeB.data.area_name;
                        return nameA > nameB ? 1 : -1;
                    },
                    headerName: 'Component / Title',
                    cellStyle: { textAlign: 'center' },
                    cellRendererParams: {
                        innerRenderer: ({ data }) => escapeHTML(data.title || data.area_name),
                    },
                },
                getDataPath: (data) => data.id_hierarchy,
                gridApi: null,
                expanded: true,
            };
        },
        mounted() {
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            this.fetchResponses().then(() => {
                this.expandLevel0();
            });
        },
        computed: {
            tabs() {
                return [
                    {
                        title: 'Responses',
                        route: '/app/responses/list/',
                        active: true,
                    },
                ];
            },
            columnDefs() {
                return [
                    {
                        field: 'area_name',
                        headerName: 'Component',
                        hide: true,
                    },
                    {
                        field: 'title',
                        headerName: 'Title',
                        hide: true,
                    },
                    {
                        field: 'is_default',
                        headerName: 'Type',
                        minWidth: 100,
                        cellRenderer: ({ data }) =>
                            data && data.response_id ? (data.is_default ? 'Default' : 'User') : '',
                    },
                    {
                        field: '',
                        headerName: '',
                        sortable: false,
                        cellRenderer: 'actionsCellRenderer',
                        minWidth: 135,
                        maxWidth: 135,
                        cellRendererParams: ({ node }) => {
                            return {
                                onReset: async (id, hideModal, responseName) => {
                                    try {
                                        this.busy = true;
                                        await api.post(`${RESPONSES_DATA_ENDPOINT(id)}reset/`);
                                        hideModal()
                                        this.successToast({
                                            text: `Response "${responseName}" successfully reset.`,
                                        });
                                    } catch (e) {
                                        const { generic } = formatResponseError(e);
                                        this.errorToast({
                                            text: generic || 'Could not reset response',
                                        });
                                    } finally {
                                        this.busy = false;
                                    }
                                },
                                onRemove: async (id, hideModal, responseName) => {
                                    try {
                                        this.busy = true;
                                        await api.delete(`${RESPONSES_DATA_ENDPOINT(id)}`);
                                        if (node.parent.allChildrenCount === 1) {
                                            this.gridApi.applyTransaction({
                                                remove: [node, node.parent],
                                            });
                                        } else {
                                            this.gridApi.applyTransaction({
                                                remove: [node],
                                            });
                                        }
                                        hideModal()
                                        this.successToast({
                                            text: `Response "${responseName}" successfully deleted.`,
                                        });
                                    } catch (e) {
                                        const { generic } = formatResponseError(e);
                                        this.errorToast({
                                            text: generic || 'Could not remove response',
                                        });
                                    } finally {
                                        this.busy = false;
                                    }
                                },
                            };
                        },
                    },
                ];
            },
        },
        methods: {
            async fetchResponses() {
                try {
                    this.busy = true;
                    const { data } = await api.get(RESPONSES_DATA_ENDPOINT());
                    this.rowData = data;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not fetch responses',
                    });
                } finally {
                    this.busy = false;
                }
            },
            createResponse() {
                this.$router.push(`/app/responses/edit/0`);
            },
            onButtonExpand() {
                if (this.expanded) {
                    this.gridApi.collapseAll();
                    this.expanded = false;
                } else {
                    this.expandLevel0();
                }
            },
            expandLevel0() {
                this.gridApi.forEachNode((node) => {
                    if (node.level === 0) {
                        node.setExpanded(true);
                    }
                });
                this.expanded = true;
            },
        },
    };
</script>

<style></style>
